import React, { ChangeEvent, useEffect, useState } from "react";
import { useCartStore } from "../store/cartStore";
import { getCart, addToCart, removeFromCart, clearCartOnServer, transformProductsToCartItems } from "../services/cartService";
import { Product, Warehouse } from "../types";
import { Link, useNavigate } from "react-router-dom";
import { Order } from "../types";
import { postOrders } from "../services/orderService";
import { fetchPickupPoints, PickupPoint } from "../services/pickupPointsService";
import Loader from "../components/loader/Loader";
import ProductCard from "../components/product/ProductCard";
import Notification from "../components/toast/Notification";
import { useUserStore } from "../store/userStore";
import { useWarehouseStore } from "../store/warehouseStore";
import { checkStockAvailability } from "../services/stockService";
import { useWarehouseInitialization } from "../hooks/useWarehouseInitialization";
// Убрали старую логику остатков по городам - теперь используется система складов

// Импортируем нужные методы из authService — часть кода из вашего LoginForm
import {
  isPhoneInDatabase,
  register,
  verifyPhone,
  login,
  resendCode,
  requestPasswordReset,
  guestRegister,
} from "../services/authService";

const CartPage = () => {
  const navigate = useNavigate();
  const { user } = useUserStore();
  const isLoggedIn = !!localStorage.getItem("token");

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notification, setNotification] = useState<{ id: number, text: string; status: "success" | "error" | "info" } | null>(null);

  const { items, setCart, clearCart, updateAllItemStocks } = useCartStore();
  const { selectedWarehouse, setSelectedWarehouse, allWarehouses, loadAllWarehouses } = useWarehouseStore();
  const { defaultWarehouse, isInitialized } = useWarehouseInitialization();
  // Убрали старую логику остатков по городам - теперь используется система складов

  // ------------------ Состояния для оформления заказа ------------------
  const [isCheckoutStarted, setIsCheckoutStarted] = useState(false);
  const [deliveryMethod, setDeliveryMethod] = useState<"delivery" | "pickup">("delivery");
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [showPickupModal, setShowPickupModal] = useState(false);
  const [deliveryAddress, setDeliveryAddress] = useState({
    city: "astana", // Фиксированный город для доставки
    street: "",
    house: "",
    apartment: "",
    entrance: "",
    floor: "",
    intercom: "",
  });
  const [selectedPickupPoint, setSelectedPickupPoint] = useState<PickupPoint>();
  const [pickupPoints, setPickupPoints] = useState<PickupPoint[]>([]);
  const [comments, setComments] = useState("");

  // Здесь храним телефон и имя — они у вас уже есть
  const [phone, setPhone] = useState(user?.phone || localStorage.getItem("uphone") || "");
  const [name, setName] = useState(user?.name || localStorage.getItem("uname") || "");

  const [personalDataAgree, setPersonalDataAgree] = useState(false);
  const [marketingAgree, setMarketingAgree] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<"kaspi" | "cash">("kaspi");
  const [isOrderPlaced, setIsOrderPlaced] = useState(false);
  const [stockWarning, setStockWarning] = useState<{
    show: boolean;
    message: string;
    unavailableItems: Array<{ name: string; requested: number; available: number }>;
  }>({
    show: false,
    message: "",
    unavailableItems: []
  });

  // -------- Блок состояний и логики для верификации номера, если не авторизован --------
  // Понадобятся:
  const [showPhoneVerifyModal, setShowPhoneVerifyModal] = useState(false); // показывать ли окно верификации
  const [verificationStep, setVerificationStep] = useState<"phone" | "verifyCode" | "register">("phone");
  const [verificationCode, setVerificationCode] = useState("");
  const [tempPassword, setTempPassword] = useState(""); // если будете регистрировать нового юзера
  const [isCodeResendDisabled, setIsCodeResendDisabled] = useState(false);
  const [resendTimer, setResendTimer] = useState(30);
  const [phoneVerified, setPhoneVerified] = useState(false); // Флаг, что номер подтверждён
  // Флаг, что пользователь нажал «Оформить заказ», но ещё не подтвердил телефон:
  const [pendingOrder, setPendingOrder] = useState(false);

  // ---------------------------------------------------------------------

  useEffect(() => {
    if (user?.phone) {
      setPhone(user.phone);
      localStorage.setItem("uphone", user.phone);
    }
    if (user?.name) {
      setName(user.name);
      localStorage.setItem("uname", user.name);
    }
  }, [user]);

  useEffect(() => {
    const syncCart = async () => {
      try {
        if (isLoggedIn) {
          // Используем текущий выбранный склад или склад по умолчанию
          const currentWarehouseId = selectedWarehouse?.id || defaultWarehouse?.id || 1;
          
          const serverResponse = await getCart(10, 0, currentWarehouseId);
          const serverCart = serverResponse.result;
          const localCart = JSON.parse(localStorage.getItem("cart") || "[]");

          for (const localItem of localCart) {
            const existingOnServer = serverCart.find((srvItem: any) => srvItem.id === localItem.id);
            if (existingOnServer) {
              const delta = localItem.quantity - (existingOnServer.quantity ?? 0);
              if (delta > 0) {
                await addToCart(localItem.id, delta);
              }
            } else {
              await addToCart(localItem.id, localItem.quantity);
            }
          }

          const updatedResponse = await getCart(10, 0, currentWarehouseId);
          setCart(transformProductsToCartItems(updatedResponse.result));
          localStorage.removeItem("cart");
        } else {
          const localCart = JSON.parse(localStorage.getItem("cart") || "[]");
          // Преобразуем локальные данные в CartItem[]
          const transformedLocalCart = localCart.map((item: any) => ({
            ...item,
            quantity: item.quantity || 1,
            // Для локальной корзины используем stock_quantity если есть, иначе is_available
            available_quantity: item.stock_quantity !== undefined 
              ? item.stock_quantity 
              : (item.is_available ? (item.quantity || 1) : 0),
            current_warehouse_id: item.current_warehouse_id,
            is_low_stock: (item.quantity || 1) > (item.stock_quantity || 0)
          }));
          setCart(transformedLocalCart);
        }
      } catch (err) {
        console.error("Ошибка при синхронизации корзины", err);
        setError("Не удалось синхронизировать корзину");
      } finally {
        setIsLoading(false);
      }
    };

    syncCart();
  }, [isLoggedIn, setCart, selectedWarehouse, defaultWarehouse]);

  useEffect(() => {
    const fetchPoints = async () => {
      try {
        const points = await fetchPickupPoints();
        setPickupPoints(points);
      } catch (error) {
        console.error("Ошибка при загрузке точек самовывоза:", error);
      }
    };
    
    // Загружаем точки самовывоза и все склады только после инициализации складов
    if (isInitialized) {
      fetchPoints();
      // Загружаем все склады для возможности выбора склада доставки
      if (!allWarehouses || allWarehouses.length === 0) {
        loadAllWarehouses().catch((e) => console.error("Ошибка загрузки складов:", e));
      }
    }
  }, [isInitialized, allWarehouses, loadAllWarehouses]);

  // Инициализируем склад по умолчанию при загрузке страницы
  useEffect(() => {
    if (isInitialized && allWarehouses && allWarehouses.length > 0) {
      let warehouseToSet;
      let shouldUpdateWarehouse = false;
      
      // Если выбрана доставка, устанавливаем склад с ID: 1 (Нурмагамбетова)
      if (deliveryMethod === "delivery") {
        warehouseToSet = allWarehouses.find(w => w.id === 1) || defaultWarehouse;
        // Проверяем, нужно ли обновить склад (если текущий склад не соответствует доставке)
        shouldUpdateWarehouse = !selectedWarehouse || selectedWarehouse.id !== 1;
      } else {
        // Для самовывоза используем склад по умолчанию, только если склад не выбран
        if (!selectedWarehouse) {
          warehouseToSet = defaultWarehouse;
          shouldUpdateWarehouse = true;
        }
      }
      
      if (warehouseToSet && shouldUpdateWarehouse) {
        console.log(`Устанавливаем склад для ${deliveryMethod}:`, warehouseToSet.name);
        setSelectedWarehouse(warehouseToSet);
        // Остатки товаров теперь обновляются автоматически через syncCart при изменении склада
      }
    }
  }, [isInitialized, defaultWarehouse, selectedWarehouse, setSelectedWarehouse, deliveryMethod, allWarehouses]);

  // Дополнительная проверка синхронизации склада и способа доставки
  useEffect(() => {
    // Проверяем только после полной инициализации и если есть выбранный склад
    if (isInitialized && selectedWarehouse && allWarehouses && allWarehouses.length > 0) {
      // Если выбрана доставка, но склад не соответствует складу доставки (ID: 1)
      if (deliveryMethod === "delivery" && selectedWarehouse.id !== 1) {
        const deliveryWarehouse = allWarehouses.find(w => w.id === 1);
        if (deliveryWarehouse) {
          console.log("Корректируем склад для доставки:", deliveryWarehouse.name);
          setSelectedWarehouse(deliveryWarehouse);
          // Остатки товаров теперь обновляются автоматически через syncCart при изменении склада
        }
      }
    }
  }, [deliveryMethod, selectedWarehouse, allWarehouses, isInitialized, setSelectedWarehouse]);

  // Таймер для повторной отправки кода в WhatsApp (по аналогии с вашим LoginForm)
  useEffect(() => {
    let timer: ReturnType<typeof setInterval>;
    if (isCodeResendDisabled) {
      timer = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setIsCodeResendDisabled(false);
            return 30;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isCodeResendDisabled]);

  useEffect(() => {
    if (phoneVerified && pendingOrder) {
      setPendingOrder(false);
      handlePlaceOrder();
    }
  }, [phoneVerified, pendingOrder]);

  // Форматирование номера (используем вашу функцию)
  const formatPhoneNumber = (value: string) => {
    value = value.replace(/[^\d+]/g, "");
    if (value.startsWith("8")) {
      value = "+7" + value.slice(1);
    }
    if (!value.startsWith("+7")) {
      value = "+7" + value.replace(/^\+/, "");
    }
    const formatted = value.replace(
      /(\+7)(\d{0,3})(\d{0,3})(\d{0,2})(\d{0,2})/,
      (match, p1, p2, p3, p4, p5) => {
        let result = p1;
        if (p2) result += ` (${p2}`;
        if (p3) result += `) ${p3}`;
        if (p4) result += `-${p4}`;
        if (p5) result += `-${p5}`;
        return result;
      }
    );
    return formatted;
  };

  const handlePhoneChange = (e: ChangeEvent<HTMLInputElement>) => {
    const rawPhone = e.target.value;
    const formattedPhone = formatPhoneNumber(rawPhone);
    setPhone(formattedPhone);
  };

  const handlePhoneFocus = () => {
    if (!phone.startsWith("+7")) {
      setPhone("+7 ");
    }
  };

  // --- Методы верификации номера (упрощённо, на базе кода из LoginForm) ---
  const handleCheckPhone = async () => {
    setError(null);
    const digitsOnlyPhone = phone.replace(/[^\d]/g, "").slice(1);
    console.log("digitsOnlyPhone", digitsOnlyPhone);
    if (!name.trim()) {
      setError("Введите, пожалуйста, ваше имя");
      return;
    }
    if (digitsOnlyPhone.length !== 10) {
      setError("Неверный формат номера (должно быть 10 цифр после +7)");
      return;
    }

    try {
      if (digitsOnlyPhone.length !== 10) {
        setError("Введите корректный номер (10 цифр после +7).");
        return;
      }
      const isRegistered = await isPhoneInDatabase(digitsOnlyPhone);
      if (!isRegistered) {
        await guestRegister({
          name: name.trim(),
          phone: digitsOnlyPhone
        });
      }
      // Переходим на шаг ввода кода
      setVerificationStep("verifyCode");
      setIsCodeResendDisabled(true);
    } catch (err) {
      setError("Ошибка проверки/регистрации номера телефона");
    }
  };

  const handleVerifyCode = async () => {
    setError(null);
    const digitsOnlyPhone = phone.replace(/[^\d]/g, "").slice(1);
    if (!verificationCode.trim()) {
      setError("Введите код из WhatsApp");
      return;
    }
    try {
      await verifyPhone({ phone: digitsOnlyPhone, code: verificationCode });
      // Если код верен, помечаем номер как "верифицированный"
      setPhoneVerified(true);
      setShowPhoneVerifyModal(false);
    } catch (err) {
      setError("Неверный код подтверждения");
    }
  };

  const handleResendCode = async () => {
    setError(null);
    const digitsOnlyPhone = phone.replace(/[^\d]/g, "").slice(1);
    try {
      await resendCode({ phone: digitsOnlyPhone });
      setIsCodeResendDisabled(true);
    } catch (err) {
      setError("Ошибка при повторной отправке кода");
    }
  };

  // ------------- Оформление заказа -----------------------
  const handleCheckoutStart = () => {
    setIsCheckoutStarted(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Обработчик смены метода доставки с автоматической сменой склада
  const handleDeliveryMethodChange = async (method: "delivery" | "pickup") => {
    setDeliveryMethod(method);
    
    if (method === "delivery" && allWarehouses && allWarehouses.length > 0) {
      // При выборе доставки устанавливаем склад с ID: 1 (Нурмагамбетова)
      const deliveryWarehouse = allWarehouses.find(w => w.id === 1);
      if (deliveryWarehouse && deliveryWarehouse.id !== selectedWarehouse?.id) {
        setSelectedWarehouse(deliveryWarehouse);
        // Остатки товаров теперь обновляются автоматически через syncCart при изменении склада
      }
    }
    // При выборе самовывоза пользователь сам выберет точку самовывоза
  };

  const handleOpenDeliveryModal = () => {
    setShowDeliveryModal(true);
  };

  const handleCloseDeliveryModal = () => {
    setShowDeliveryModal(false);
  };

  const handleOpenPickupModal = () => {
    // Загружаем склады, если ещё не загружены
    if (!allWarehouses || allWarehouses.length === 0) {
      loadAllWarehouses().catch((e) => console.error("Ошибка загрузки складов:", e));
    }
    setShowPickupModal(true);
  };

  const handleClosePickupModal = () => {
    setShowPickupModal(false);
  };

  const handleCloseStockWarning = () => {
    setStockWarning({ show: false, message: "", unavailableItems: [] });
  };

  const handleSaveDeliveryAddress = () => {
    // Убрали старую логику остатков по городам - теперь используется система складов
    setShowDeliveryModal(false);
  };

  // Выбор склада из модалки самовывоза
  const handleSelectWarehouse = async (warehouse: Warehouse) => {
    try {
      setSelectedWarehouse(warehouse);

      // Обновляем остатки товаров для выбранного склада
      await updateAllItemStocks(warehouse.id);

      // Проверяем доступность товаров на выбранном складе
      const stockItems = items.map((item) => ({
        product_id: item.id,
        quantity: item.quantity || 0,
        price: item.price,
      }));

      if (stockItems.length > 0) {
        const availabilityResponse = await checkStockAvailability(warehouse.id, stockItems);
        const unavailableItems = availabilityResponse.result.unavailable_items
          .filter((item: { product_id: number; requested_qty: number; available_qty: number; is_out_of_stock: boolean }) => item.available_qty < item.requested_qty)
          .map((item: { product_id: number; requested_qty: number; available_qty: number; is_out_of_stock: boolean }) => {
            const product = items.find((p) => p.id === item.product_id);
            return {
              name: product?.name || "Неизвестный товар",
              requested: item.requested_qty,
              available: item.available_qty,
            };
          });

        if (unavailableItems.length > 0) {
          setStockWarning({
            show: true,
            message: `В магазине "${warehouse.name}" недостаточно товаров:`,
            unavailableItems,
          });
        } else {
          setStockWarning({ show: false, message: "", unavailableItems: [] });
        }
      }

      setShowPickupModal(false);
    } catch (e) {
      console.error("Ошибка при выборе магазина:", e);
      setShowPickupModal(false);
    }
  };

  const handleSavePickupPoint = async (point: PickupPoint) => {
    try {
      // Устанавливаем выбранный склад
      const warehouse = {
        id: point.warehouse_id,
        name: point.warehouse?.name || 'Не указан',
        prosklad_id: 0, // Временное значение, так как не используется в данном контексте
        employee_id: '', // Временное значение, так как не используется в данном контексте
        is_active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      setSelectedWarehouse(warehouse);

      // Обновляем остатки товаров для нового склада
      try {
        await updateAllItemStocks(warehouse.id);
      } catch (error) {
        console.error("Ошибка при обновлении остатков товаров:", error);
      }

      // Проверяем доступность товаров на выбранном складе
      const stockItems = items.map(item => ({
        product_id: item.id,
        quantity: item.quantity || 0,
        price: item.price
      }));

      if (stockItems.length > 0) {
        const availabilityResponse = await checkStockAvailability(point.warehouse_id, stockItems);
        
        // Проверяем, есть ли товары с недостаточным количеством
        const unavailableItems = availabilityResponse.result.unavailable_items
          .filter((item: { product_id: number; requested_qty: number; available_qty: number; is_out_of_stock: boolean }) => item.available_qty < item.requested_qty)
          .map((item: { product_id: number; requested_qty: number; available_qty: number; is_out_of_stock: boolean }) => {
            const product = items.find(p => p.id === item.product_id);
            return {
              name: product?.name || 'Неизвестный товар',
              requested: item.requested_qty,
              available: item.available_qty
            };
          });

        if (unavailableItems.length > 0) {
          setStockWarning({
            show: true,
            message: `В магазине "${point.warehouse?.name || 'Не указан'}" недостаточно товаров:`,
            unavailableItems
          });
        } else {
          setStockWarning({ show: false, message: "", unavailableItems: [] });
        }
      }

      setSelectedPickupPoint(point);
      setShowPickupModal(false);
    } catch (error) {
      console.error("Ошибка при проверке доступности товаров:", error);
      // В случае ошибки всё равно устанавливаем точку самовывоза
      setSelectedPickupPoint(point);
      setShowPickupModal(false);
    }
  };

  const isFormValid = () => {
    if (!phone.trim() || !name.trim()) return false;
    if (!personalDataAgree) return false;
    if (!deliveryMethod) return false;
    if (!paymentMethod) return false;

    if (deliveryMethod === "delivery") {
      const { street, house } = deliveryAddress;
      if (!street.trim() || !house.trim()) {
        return false;
      }
      // В примере у вас только kaspi для доставки
      if (paymentMethod !== "kaspi") {
        return false;
      }
    }

    if (deliveryMethod === "pickup") {
      // Достаточно выбранного склада (pickupPoint больше не обязателен)
      if (!selectedWarehouse && !selectedPickupPoint) return false;
    }

    return true;
  };

  const handlePlaceOrder = async () => {
    console.log("Оформление заказа...");
    // Для неавторизованных пользователей сначала проверяем, есть ли телефон в базе
    if (!isLoggedIn) {
      const digitsOnlyPhone = phone.replace(/[^\d]/g, "").slice(1);
      try {
        const phoneExists = await isPhoneInDatabase(digitsOnlyPhone);
        // Если телефон не зарегистрирован – открываем окно подтверждения номера
        if (!phoneExists) {
          setPendingOrder(true);
          setShowPhoneVerifyModal(true);
          return;
        }
      } catch (err) {
        setError("Ошибка проверки номера телефона");
        return;
      }
    }

    if (!isFormValid()) {
      return;
    }

    // Финальная проверка доступности товаров перед оформлением заказа
    try {
      const currentWarehouseId = selectedWarehouse?.id || defaultWarehouse?.id || 1;
      const availabilityItems = items.map(item => ({
        product_id: item.id,
        quantity: item.quantity || 0,
        price: item.price
      }));

      const availabilityResponse = await checkStockAvailability(currentWarehouseId, availabilityItems);
      
      if (!availabilityResponse.result.available) {
        const unavailableItems = availabilityResponse.result.unavailable_items;
        const errorMessage = unavailableItems
          .map(item => {
            const product = items.find(p => p.id === item.product_id);
            const productName = product?.name || `Товар ID ${item.product_id}`;
            if (item.is_out_of_stock) {
              return `${productName}: нет в наличии`;
            } else {
              return `${productName}: доступно ${item.available_qty}, запрошено ${item.requested_qty}`;
            }
          })
          .join('; ');

        setNotification({ 
          id: Date.now(), 
          text: `Товары недоступны: ${errorMessage}`, 
          status: "error" 
        });
        return;
      }
    } catch (availabilityError) {
      console.error("Ошибка проверки доступности:", availabilityError);
      setNotification({ 
        id: Date.now(), 
        text: "Не удалось проверить доступность товаров. Попробуйте еще раз.", 
        status: "error" 
      });
      return;
    }

    setIsOrderPlaced(true);

    const totalAmount = items.reduce(
      (sum, item) => sum + item.price * (item.quantity ?? 0),
      0
    );

    const digitsOnlyPhone = phone.replace(/[^\d]/g, "").slice(1);
    const normalizedComments = comments.replace(/(\r\n|\n|\r)/gm, " ");

    try {
      let requestBody: Partial<Order>;

      if (isLoggedIn) {
        // Если пользователь залогинен, всё как обычно
        requestBody = {
          warehouse_id: selectedWarehouse?.id || defaultWarehouse?.id,
          pickup_point_id: Number(selectedPickupPoint?.id),
          delivery_type: deliveryMethod,
          payment_method: paymentMethod,
          comment: normalizedComments,
          guest_name: name,
          items: items.map((item) => ({
            product_id: item.id,
            quantity: item.quantity ?? 0,
            price: item.price,
          })),
          total_amount: totalAmount,
          ...(deliveryMethod === "delivery" && { address: deliveryAddress }),
        };
      } else {
        // Если не залогинен, но прошли верификацию номера
        requestBody = {
          guest_name: name,
          guest_phone: digitsOnlyPhone,
          delivery_type: deliveryMethod,
          payment_method: paymentMethod,
          warehouse_id: selectedWarehouse?.id || defaultWarehouse?.id,
          pickup_point_id: Number(selectedPickupPoint?.id),
          comment: normalizedComments,
          items: items.map((item) => ({
            product_id: item.id,
            quantity: item.quantity ?? 0,
            price: item.price,
          })),
          total_amount: totalAmount,
          ...(deliveryMethod === "delivery" && { address: deliveryAddress }),
        };
      }

      await postOrders(requestBody);

      // Очистка корзины
      if (isLoggedIn) {
        await clearCartOnServer();
      }
      localStorage.removeItem("cart");
      clearCart();

      setDeliveryAddress({
        city: "astana", // Фиксированный город для доставки
        street: "",
        house: "",
        apartment: "",
        entrance: "",
        floor: "",
        intercom: "",
      });
      setComments("");
      setPhone("");
      setName("");
      setPersonalDataAgree(false);
      setMarketingAgree(false);
      setPaymentMethod("kaspi");
      setDeliveryMethod("delivery");
      setSelectedPickupPoint(undefined);
      setIsCheckoutStarted(false);

      setNotification({ id: Date.now(), text: "Благодарим за заказ. В ближайшее время с вами свяжется наш менеджер для более подробного оформления заказа☺️", status: "success" });
      setTimeout(() => {
        navigate("/catalog");
      }, 5000);
    } catch (error) {
      console.error("Ошибка оформления заказа:", error);
      setNotification({ id: Date.now(), text: "Произошла ошибка при оформлении заказа. Попробуйте еще раз.", status: "error" });
    }
  };

  if (isLoading) return <Loader />;

  // Фильтруем товары с учетом их наличия на текущем складе
  const availableItems = items.filter((item) => {
    const hasStockData = item.available_quantity !== undefined;
    return hasStockData 
      ? (item.available_quantity || 0) > 0 
      : item.is_available !== false; // Считаем доступным, если is_available не false
  });
  const totalQuantity = availableItems.reduce((sum, item) => sum + (item.quantity ?? 0), 0);
  const totalPrice = availableItems.reduce((sum, item) => sum + item.price * (item.quantity ?? 0), 0);

  return (
    <div className="container mx-auto p-4 flex flex-col md:flex-row gap-6">

      {notification && <Notification id={notification.id || Date.now()} text={notification.text} status={notification.status} duration={5000} />}

      {/* Левая колонка */}
      <div className="flex-1 border rounded-lg p-6 mb-6 md:mb-0">
        {!isCheckoutStarted ? (
          <>
            <h2 className="text-2xl font-bold mb-4">Корзина</h2>
            {items.length === 0 ? (
              <div>
                <p>Пока ничего не выбрано... Не упустите свои любимые товары!</p>
                <div className="flex flex-col items-center mt-8">
                  {/* Ваши иконки и прочее */}
                  <button
                    className="bg-black text-white px-4 py-2 mt-4 w-full"
                    onClick={() => navigate("/catalog")}
                  >
                    Перейти в каталог
                  </button>
                </div>
              </div>
            ) : (
              <>
                <p className="text-sm text-gray-500 mb-4">{totalQuantity} товаров</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {items.map((item: Product) => (
                    <ProductCard key={item.id} product={item} />
                  ))}
                </div>
              </>
            )}
          </>
        ) : (
          // Форма оформления заказа
          <div>
            <div className="mb-4">
              <h2 className="text-2xl font-bold mb-2">Оформление заказа</h2>
              {deliveryMethod === "pickup" && (
                <p className="text-sm text-gray-600">
                  Самовывоз с: <span className="font-medium">
                    {selectedWarehouse ? selectedWarehouse.name : (defaultWarehouse ? defaultWarehouse.name : 'Загрузка...')}
                  </span>
                </p>
              )}
              {deliveryMethod === "delivery" && (
                <p className="text-sm text-gray-600">
                  Доставка по городу Астана
                </p>
              )}
            </div>

            {/* Выбор самовывоза или доставки */}
            <div className="mb-4">
              <label className="font-bold block mb-2">Способ получения:</label>
              <div className="flex gap-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    className="mr-2"
                    name="deliveryMethod"
                    value="delivery"
                    checked={deliveryMethod === "delivery"}
                    onChange={() => handleDeliveryMethodChange("delivery")}
                  />
                  Доставка
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    className="mr-2"
                    name="deliveryMethod"
                    value="pickup"
                    checked={deliveryMethod === "pickup"}
                    onChange={() => handleDeliveryMethodChange("pickup")}
                  />
                  Самовывоз
                </label>
              </div>
            </div>

            {deliveryMethod === "delivery" && (
              <div className="mb-4">
                {!deliveryAddress.street && !deliveryAddress.house ? (
                  <button
                    className="bg-black text-white px-4 py-2 rounded"
                    onClick={handleOpenDeliveryModal}
                  >
                    Ввести адрес доставки
                  </button>
                ) : (
                  <div className="border p-2 rounded">
                    <p>
                      ул. {deliveryAddress.street}, д. {deliveryAddress.house}
                      {deliveryAddress.apartment && `, кв. ${deliveryAddress.apartment}`}
                    </p>
                    {deliveryAddress.entrance && (
                      <p>
                        Подъезд: {deliveryAddress.entrance}, Этаж: {deliveryAddress.floor}, Домофон: {deliveryAddress.intercom}
                      </p>
                    )}
                    <button
                      className="text-blue-500 underline mt-2"
                      onClick={handleOpenDeliveryModal}
                    >
                      Изменить
                    </button>
                  </div>
                )}
              </div>
            )}

            {deliveryMethod === "pickup" && (
              <div className="mb-4">
                {!selectedPickupPoint ? (
                  <button
                    className="bg-black text-white px-4 py-2 rounded"
                    onClick={handleOpenPickupModal}
                  >
                    Выбрать точку самовывоза
                  </button>
                ) : (
                  <div className="border p-2 rounded">
                    <p className="font-medium">{selectedPickupPoint.name}</p>
                    <p className="text-sm text-gray-600">
                      {selectedPickupPoint.warehouse?.name || 'Склад'}, {selectedPickupPoint.address.street}, {selectedPickupPoint.address.house}
                    </p>
                    {stockWarning.show && (
                      <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded">
                        <p className="text-sm text-orange-700 font-medium">⚠️ Внимание: недостаточно товаров в магазине</p>
                        <button
                          className="text-sm text-orange-600 underline mt-1"
                          onClick={() => setStockWarning({ show: true, message: stockWarning.message, unavailableItems: stockWarning.unavailableItems })}
                        >
                          Подробнее
                        </button>
                      </div>
                    )}
                    <button
                      className="text-blue-500 underline mt-2"
                      onClick={handleOpenPickupModal}
                    >
                      Изменить
                    </button>
                  </div>
                )}
              </div>
            )}

            <div className="mb-4">
              <label className="block mb-1">Комментарий к заказу:</label>
              <textarea
                className="border p-2 w-full"
                rows={3}
                value={comments}
                onChange={(e) => setComments(e.target.value)}
              />
            </div>

            {/* Поля имени и телефона */}
            <div className="mb-4">
              <label className="block mb-1">Номер телефона *</label>
              <input
                type="text"
                value={phone}
                onChange={handlePhoneChange}
                onFocus={handlePhoneFocus}
                placeholder="Номер телефона"
                maxLength={18}
                className="w-full border rounded-lg px-4 py-2 mb-4 focus:outline-none focus:ring-[#008EFF] focus:border-[#008EFF]"
              />

              <label className="block mb-1">Имя *</label>
              <input
                className="border p-2 w-full mb-2"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />

              <div className="flex items-center mb-2">
                <input
                  className="mr-2"
                  type="checkbox"
                  checked={personalDataAgree}
                  onChange={() => setPersonalDataAgree((prev) => !prev)}
                />
                <span className="text-sm">
                  Я даю согласие на обработку персональных данных...
                </span>
              </div>

              <div className="flex items-center">
                <input
                  className="mr-2"
                  type="checkbox"
                  checked={marketingAgree}
                  onChange={() => setMarketingAgree((prev) => !prev)}
                />
                <span className="text-sm">
                  Я даю согласие на получение рекламных рассылок...
                </span>
              </div>
            </div>

            {/* Способ оплаты */}
            {deliveryMethod && (
              <div className="mb-4">
                <label className="font-bold block mb-2">Способ оплаты:</label>
                {deliveryMethod === "pickup" ? (
                  <div className="flex flex-col md:flex-row gap-4 ">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="kaspi"
                        checked={paymentMethod === "kaspi"}
                        onChange={() => setPaymentMethod("kaspi")}
                        className="mr-2"
                      />
                      Выставить счет на Kaspi
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="cash"
                        checked={paymentMethod === "cash"}
                        onChange={() => setPaymentMethod("cash")}
                        className="mr-2"
                      />
                      Оплата в магазине
                    </label>
                  </div>
                ) : (
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="kaspi"
                      checked={paymentMethod === "kaspi"}
                      onChange={() => setPaymentMethod("kaspi")}
                      className="mr-2"
                    />
                    Выставить счет на Kaspi
                  </label>
                )}
              </div>
            )}

            <button
              className={`w-full bg-black text-white px-4 py-2 mt-4 rounded ${isFormValid() ? "opacity-100" : "opacity-50 cursor-not-allowed"
                }`}
              disabled={!isFormValid()}
              onClick={handlePlaceOrder}
            >
              Оформить заказ
            </button>
          </div>
        )}
      </div>

      {/* Правая колонка: итоги и кнопка "Продолжить" */}
      <div className="w-full md:w-1/3 border rounded-lg p-3 bg-gray-100 self-start">
        <h3 className="text-xl font-bold mb-4">Товары</h3>
        
        {/* Информация о текущем складе */}
        {deliveryMethod === "pickup" && (
          <div className="flex gap-2 mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
            <p className="text-sm font-medium text-blue-800">Самовывоз с:</p>
            {selectedWarehouse ? (
              <>
                <p className="text-sm text-blue-700">{selectedWarehouse.name}</p>
                {stockWarning.show && (
                  <p className="text-sm text-orange-600 mt-1">⚠️ Недостаточно товаров</p>
                )}
              </>
            ) : (
              <p className="text-sm text-gray-600">
                {defaultWarehouse ? defaultWarehouse.name : 'Загрузка...'}
              </p>
            )}
          </div>
        )}
        
        {/* Информация о доставке */}
        {deliveryMethod === "delivery" && (
          <div className="flex gap-2 mb-4 p-3 bg-green-50 border border-green-200 rounded">
            <p className="text-sm font-medium text-green-800">Доставка:</p>
            <p className="text-sm text-green-700">По городу Астана</p>
          </div>
        )}
        {items && items.length > 0 ? (
          <>
            <ul className="space-y-2">
              {items.map((item) => (
                <li key={item.id} className="flex justify-between gap-4 pb-[10px] border-b border-gray-200">
                  <span>{item.name}</span>
                  <span className="flex items-center gap-1 whitespace-nowrap">
                    {(() => {
                      const hasStockData = item.available_quantity !== undefined;
                      const isOutOfStock = hasStockData 
                        ? item.available_quantity === 0 
                        : !item.is_available;
                      const isLowStock = hasStockData 
                        ? (item.quantity || 0) > (item.available_quantity || 0)
                        : false;
                      
                      return (
                        <>
                          <span className={deliveryMethod === "pickup" && (isOutOfStock || isLowStock) ? 'text-orange-600' : ''}>
                            {item.quantity} шт
                          </span>
                          {deliveryMethod === "pickup" && isOutOfStock && (
                            <span className="text-xs text-red-600">
                              (нет в наличии)
                            </span>
                          )}
                          {deliveryMethod === "pickup" && isLowStock && !isOutOfStock && (
                            <span className="text-xs text-orange-600">
                              (доступно: {item.available_quantity})
                            </span>
                          )}
                        </>
                      );
                    })()}
                  </span>
                </li>
              ))}
            </ul>
            <div className="flex justify-between font-bold text-lg">
              <span>Итого</span>
              <span>{totalPrice} ₸</span>
            </div>
            {!isCheckoutStarted ? (
              <button
                className="w-full bg-black text-white px-4 py-2 mt-4 rounded"
                onClick={handleCheckoutStart}
              >
                Продолжить заказ
              </button>
            ) : (
              <div className="mt-4 text-gray-600 text-sm">
                Заполните форму для оформления заказа
              </div>
            )}
          </>
        ) : (
          <div>Здесь будут ваши товары</div>
        )}
      </div>

      {/* Модальное окно для ввода адреса доставки */}
      {showDeliveryModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded w-11/12 md:w-96">
            <h4 className="text-lg font-bold mb-4">Введите адрес доставки</h4>
            {/* <div className="mb-2">
              <label className="block mb-1">Город:</label>
              <div className="border p-2 w-full bg-gray-50 text-gray-700">
                Адрес доставки
              </div>
            </div> */}

            <div className="mb-2">
              <label className="block mb-1">Улица:</label>
              <input
                type="text"
                className="border p-1 w-full"
                value={deliveryAddress.street}
                onChange={(e) =>
                  setDeliveryAddress((prev) => ({ ...prev, street: e.target.value }))
                }
              />
            </div>
            <div className="mb-2">
              <label className="block mb-1">Дом:</label>
              <input
                type="text"
                className="border p-1 w-full"
                value={deliveryAddress.house}
                onChange={(e) =>
                  setDeliveryAddress((prev) => ({ ...prev, house: e.target.value }))
                }
              />
            </div>
            <div className="mb-2">
              <label className="block mb-1">Квартира:</label>
              <input
                type="text"
                className="border p-1 w-full"
                value={deliveryAddress.apartment}
                onChange={(e) =>
                  setDeliveryAddress((prev) => ({ ...prev, apartment: e.target.value }))
                }
              />
            </div>
            <div className="mb-2">
              <label className="block mb-1">Подъезд:</label>
              <input
                type="text"
                className="border p-1 w-full"
                value={deliveryAddress.entrance}
                onChange={(e) =>
                  setDeliveryAddress((prev) => ({ ...prev, entrance: e.target.value }))
                }
              />
            </div>
            <div className="mb-2">
              <label className="block mb-1">Этаж:</label>
              <input
                type="text"
                className="border p-1 w-full"
                value={deliveryAddress.floor}
                onChange={(e) =>
                  setDeliveryAddress((prev) => ({ ...prev, floor: e.target.value }))
                }
              />
            </div>
            <div className="mb-2">
              <label className="block mb-1">Домофон:</label>
              <input
                type="text"
                className="border p-1 w-full"
                value={deliveryAddress.intercom}
                onChange={(e) =>
                  setDeliveryAddress((prev) => ({ ...prev, intercom: e.target.value }))
                }
              />
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                className="px-4 py-2 bg-gray-200 rounded"
                onClick={handleCloseDeliveryModal}
              >
                Закрыть
              </button>
              <button
                className="px-4 py-2 bg-black text-white rounded"
                onClick={handleSaveDeliveryAddress}
              >
                Сохранить
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно: выбор склада для самовывоза */}
      {showPickupModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded w-11/12 md:w-96">
            <h4 className="text-lg font-bold mb-4">Выберите адрес самовывоза</h4>
            <ul className="mb-4">
              {(allWarehouses || []).map((w) => (
                <li
                  key={w.id}
                  className="mb-2 cursor-pointer hover:bg-gray-100 p-3 rounded border"
                  onClick={() => handleSelectWarehouse(w)}
                >
                  <div className="font-medium">{w.name}</div>
                  {/* <div className="text-xs text-gray-500">ID: {w.id}</div> */}
                </li>
              ))}
            </ul>
            <button
              className="px-4 py-2 bg-gray-200 rounded w-full"
              onClick={handleClosePickupModal}
            >
              Закрыть
            </button>
          </div>
        </div>
      )}

      {/* Модальное окно для предупреждения о нехватке товара */}
      {stockWarning.show && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded w-11/12 md:w-96">
            <h4 className="text-lg font-bold mb-4 text-orange-600">⚠️ Предупреждение о наличии</h4>
            <p className="text-gray-700 mb-4">{stockWarning.message}</p>
            <ul className="mb-4 space-y-2">
              {stockWarning.unavailableItems.map((item, index) => (
                <li key={index} className="bg-red-50 p-3 rounded border border-red-200">
                  <div className="font-medium text-red-800">{item.name}</div>
                  <div className="text-sm text-red-600">
                    Запрошено: {item.requested} шт.
                  </div>
                  <div className="text-sm text-red-600">
                    Доступно: {item.available} шт.
                  </div>
                </li>
              ))}
            </ul>
            <div className="text-sm text-gray-600 mb-4">
              Рекомендуем уменьшить количество товаров или выбрать другую точку самовывоза.
            </div>
            <button
              className="px-4 py-2 bg-orange-500 text-white rounded w-full hover:bg-orange-600"
              onClick={handleCloseStockWarning}
            >
              Понятно
            </button>
          </div>
        </div>
      )}

      {/* Модальное окно для верификации номера (откроется, если не авторизован) */}
      {showPhoneVerifyModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded w-11/12 md:w-96 relative">
            <button
              className="absolute right-2 top-2 text-xl font-bold"
              onClick={() => setShowPhoneVerifyModal(false)}
            >
              &times;
            </button>

            {verificationStep === "phone" && (
              <div>
                <h2 className="text-xl font-semibold mb-4">Подтверждение номера</h2>
                <input
                  type="text"
                  value={phone}
                  onChange={handlePhoneChange}
                  onFocus={handlePhoneFocus}
                  placeholder="Номер телефона"
                  maxLength={18}
                  className="w-full border rounded-lg px-4 py-2 mb-4 focus:outline-none"
                />
                <button
                  onClick={handleCheckPhone}
                  className="w-full bg-black text-white py-2 rounded-lg hover:bg-stone-800 transition"
                  disabled={phone.length < 18}
                >
                  Отправить код
                </button>
              </div>
            )}

            {verificationStep === "verifyCode" && (
              <div>
                <h2 className="text-xl font-semibold mb-4">Введите код из WhatsApp</h2>
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  placeholder="Код"
                  className="w-full border rounded-lg px-4 py-2 mb-4 focus:outline-none"
                />
                <button
                  onClick={handleVerifyCode}
                  className="w-full bg-black text-white py-2 rounded-lg hover:bg-stone-800 transition mb-2"
                >
                  Подтвердить
                </button>
                <button
                  disabled={isCodeResendDisabled}
                  onClick={handleResendCode}
                  className={`w-full py-2 rounded-lg transition ${isCodeResendDisabled
                    ? "bg-gray-300 text-gray-500"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                >
                  {isCodeResendDisabled
                    ? `Повторить через ${resendTimer}с`
                    : "Отправить код снова"}
                </button>
              </div>
            )}

            {error && <div className="text-red-600 mt-4 text-sm">{error}</div>}
          </div>
        </div>
      )}
    </div>
  );
};

export default CartPage;
